/* import __COLOCATED_TEMPLATE__ from './basket.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */

import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';

import type QuoteService from 'embercom/services/quote-service';
import type Quote from 'embercom/models/quote';
import { FIN_PRICE_PER_QUALIFICATION, FIN_PRICE_PER_RESOLUTION } from 'embercom/lib/billing';

export interface Args {
  quote?: Quote;
  yearlyBillingPeriodSelected: boolean;
  showDiscount: boolean;
  selectedPlanId: string;
  seatNumber: number;
  additionalSeatNumber?: number;
  selectedProactiveAddon: boolean;
  selectedCopilotAddon: boolean;
  selectedNumberOfCopilotAdmins: number;
  hasSelectedPerformanceProAddon: boolean;
  fullSeatPrice: number;
  loading: boolean;
  discountedSeatPrice: number;
  fullProactiveAddonPrice: number;
  discountedProactiveAddonPrice: number;
  fullCopilotAddonPrice: number;
  discountedCopilotAddonPrice?: number;
  fullPerformanceProAddonPrice?: number;
  discountedPerformanceProAddonPrice?: number;
  monthlyFullSeatPrice: number;
  monthlyDiscountedSeatPrice: number;
  monthlyAdditionalSeatPrice?: number;
  monthlyDiscountedAdditionalSeatPrice?: number;
  monthlyDiscountedTotal: number;
  monthlyCopilotPriceForSeats: number;
  monthlyTotal: number;
  hasCopilotEarlyStageDiscount?: boolean;
  trialEndDate?: string;
  trialEndFullPrice?: number;
  trialEndDiscountPrice?: number;
  totalCopy: string;
  totalPriceFormatted: string;
  showAdditionalSeats?: boolean;
  monthlyResolutionAllowance?: number;
  monthlyQualificationAllowance?: number;
  isEarlyStage?: boolean;
  campaignBasketDescription?: string;
  campaignFinMetricCardDescriptionKey?: string;
}

interface Signature {
  Args: Args;
  Element: HTMLDivElement;
  Blocks: {
    header?: any;
    beforeTotalBlock: any;
    afterTotalBlock?: any;
    basketButton: any;
    topOfBasket?: any;
  };
}

export default class Basket extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: any;
  @service declare quoteService: QuoteService;

  get subHeading() {
    return this.intl.t(
      `signup.teams.pricing5.annual-plans.basket.subheading.${this.args.selectedPlanId}`,
    );
  }

  get additionalSeatsExplanation() {
    return this.intl.t(
      'signup.teams.pricing5.annual-plans.basket.additional-seats-explanation-v2',
      { fullSeatNumber: this.args.seatNumber },
    );
  }

  get metricsTitle() {
    if (this.args.isEarlyStage) {
      return 'signup.teams.pricing5.annual-plans.metrics.title-early-stage';
    }

    return 'signup.teams.pricing5.annual-plans.metrics.title';
  }

  get finMetricCardDescription() {
    if (this.args.isEarlyStage) {
      if (this.args.monthlyQualificationAllowance) {
        return 'signup.teams.pricing5.annual-plans.metrics.fin.description-outcome-early-stage-with-qualifications';
      }

      return 'signup.teams.pricing5.annual-plans.metrics.fin.description-outcome-early-stage';
    }

    if (this.args.campaignFinMetricCardDescriptionKey) {
      return this.args.campaignFinMetricCardDescriptionKey;
    }

    if (this.appService.app.canUseSalesAgentConversionFlow) {
      return 'signup.teams.pricing5.annual-plans.metrics.fin.description-outcome-sales-agent-conversion';
    }

    return 'signup.teams.pricing5.annual-plans.metrics.fin.description-outcome';
  }

  get finMetricCardTooltip() {
    if (this.appService.app.canUseSalesAgentConversionFlow) {
      return 'signup.teams.pricing5.annual-plans.metrics.fin.tooltip-outcome-sales-agent-conversion';
    }

    return 'signup.teams.pricing5.annual-plans.metrics.fin.tooltip-outcome';
  }

  get finMetricCardTooltipArticleTitle() {
    if (this.appService.app.canUseSalesAgentConversionFlow) {
      return 'signup.teams.pricing5.annual-plans.metrics.fin.about-outcomes';
    }

    return 'signup.teams.pricing5.annual-plans.metrics.fin.learn-more-about-outcomes';
  }

  get basketResolutionsLabel() {
    return 'signup.teams.pricing5.annual-plans.basket.resolutions-allowance-outcome';
  }

  get resolutionPrice() {
    return FIN_PRICE_PER_RESOLUTION;
  }

  get qualificationPrice() {
    return FIN_PRICE_PER_QUALIFICATION;
  }

  get actualSeatPrice() {
    return this.args.showDiscount ? this.args.discountedSeatPrice : this.args.fullSeatPrice;
  }

  get isProactiveAddonDiscounted() {
    return this.args.fullProactiveAddonPrice > this.args.discountedProactiveAddonPrice;
  }

  get isPerformanceProAddonDiscounted() {
    return (
      this.args.fullPerformanceProAddonPrice !== undefined &&
      this.args.discountedPerformanceProAddonPrice !== undefined &&
      this.args.fullPerformanceProAddonPrice > this.args.discountedPerformanceProAddonPrice
    );
  }

  private formatDisplayPrice(price: number): string | number {
    return price % 1 === 0 ? price : price.toFixed(2);
  }

  get formattedMonthlyDiscountedSeatPrice() {
    return this.formatDisplayPrice(this.args.monthlyDiscountedSeatPrice);
  }

  get formattedMonthlyDiscountedAdditionalSeatPrice() {
    return this.formatDisplayPrice(this.args.monthlyDiscountedAdditionalSeatPrice ?? 0);
  }

  get formattedMonthlyDiscountedTotal() {
    return this.formatDisplayPrice(this.args.monthlyDiscountedTotal);
  }

  get yearlySavings() {
    return this.quoteService.formatPrice(
      this.args.monthlyTotal * 12 - this.args.monthlyDiscountedTotal * 12,
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Signup::Teams::Pricing5::Components::Basket': typeof Basket;
  }
}
